/**
 * DOM-only image cache/loader — the impure half of the art-slot system (the pure half,
 * URL resolution and the fallback decision, lives in `./art` and is unit-tested there).
 * Not unit-tested itself, same convention as `./canvas`'s own header comment: a thin
 * `Image()`/cache wrapper with no decision logic of its own.
 *
 * `preloadImage` is fire-and-forget and safe to call every frame — a cache hit (any
 * known state, including still-`"loading"`) is a no-op, so the render loop never awaits
 * or blocks on a fetch. A failed load is cached as `"error"` permanently for this page
 * load (never retried) rather than hammering a missing asset every frame.
 */

import type { ArtLoadState } from "./art";

const images = new Map<string, HTMLImageElement>();
const states = new Map<string, ArtLoadState>();

/** Kicks off loading `url` if it hasn't been attempted yet. No-op otherwise. Never
 * throws, never returns a value the caller needs to await. */
export function preloadImage(url: string): void {
  if (states.has(url)) return;
  states.set(url, "loading");
  const img = new Image();
  img.onload = () => {
    images.set(url, img);
    states.set(url, "ready");
  };
  img.onerror = () => {
    states.set(url, "error");
  };
  img.src = url;
}

export function getArtLoadState(url: string): ArtLoadState | undefined {
  return states.get(url);
}

/** The loaded image for `url`, or `null` if it's missing, still loading, or failed —
 * callers should pair this with `shouldUseFallback(getArtLoadState(url))` (`./art`)
 * rather than treating a `null` return here as the only fallback signal. */
export function getCachedImage(url: string): HTMLImageElement | null {
  return images.get(url) ?? null;
}

/** Test-only reset — `preloadImage` is otherwise permanently memoized per URL for the
 * page's lifetime, which would make repeated test runs against the same URL string
 * observe only the FIRST run's outcome. */
export function resetArtCacheForTests(): void {
  images.clear();
  states.clear();
}
